<template>
  <div class="artifact-container" ref="container">
    <div class="artifact-dropdown" @click="toggleExpanded">
      <div class="artifact-header">
        <div class="artifact-icon">
          <v-icon small>mdi-file-document</v-icon>
        </div>
        <div class="artifact-title">
          <span class="filename">{{ artifact.filename }}</span>
          <span class="filetype">{{ artifact.filetype }}</span>
        </div>
        <div class="artifact-actions">
          <button 
            class="download-btn"
            @click.stop="downloadFile"
            :title="`Download ${artifact.filename}`"
          >
            <v-icon small>mdi-download</v-icon>
          </button>
          <div class="artifact-toggle">
            <v-icon :class="{ 'expanded': isExpanded }">mdi-chevron-down</v-icon>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="isExpanded" class="artifact-content">
      <div 
        v-if="artifact.filetype === 'markdown'"
        class="artifact-markdown"
        v-html="renderedMarkdown"
      ></div>
      <div 
        v-else-if="artifact.filetype === 'html'"
        class="artifact-html"
        v-html="artifact.data"
      ></div>
      <pre v-else class="artifact-text">{{ artifact.data }}</pre>
      
      <button class="artifact-footer" @click="collapseAndScroll" aria-label="Collapse">
        <v-icon small>mdi-chevron-up</v-icon>
      </button>
    </div>
  </div>
</template>

<script>
import showdown from 'showdown';

export default {
  name: 'Artifact',
  props: {
    artifact: {
      type: Object,
      required: true,
      validator: (artifact) => {
        return artifact && 
               typeof artifact.filename === 'string' && 
               typeof artifact.filetype === 'string' && 
               typeof artifact.data === 'string';
      }
    }
  },
  data() {
    return {
      isExpanded: false,
      converter: new showdown.Converter({
        simpleLineBreaks: true,
        openLinksInNewWindow: true,
        literalMidWordUnderscores: true,
        tables: true,
        tablesHeaderId: false,
        strikethrough: true,
        tasklists: true
      })
    };
  },
  computed: {
    renderedMarkdown() {
      if (this.artifact.filetype === 'markdown') {
        return this.converter.makeHtml(this.artifact.data);
      }
      return '';
    }
  },
  methods: {
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    collapseAndScroll() {
      this.$refs.container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTimeout(() => {
        this.isExpanded = false;
      }, 150);
    },
    downloadFile() {
      // Create a blob with the artifact data
      const blob = new Blob([this.artifact.data], { type: 'text/plain' });
      
      // Create a temporary download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = this.artifact.filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  }
};
</script>

<style scoped>
.artifact-container {
  margin: 8px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.artifact-dropdown {
  cursor: pointer;
  user-select: none;
}

.artifact-header {
  display: flex;
  align-items: center;
  padding: 12px;
  gap: 8px;
}

.artifact-icon {
  color: #666;
}

.artifact-title {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.filename {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.filetype {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
}

.artifact-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.download-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.download-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.download-btn:active {
  transform: scale(0.95);
}

.artifact-toggle {
  transition: transform 0.2s ease;
}

.artifact-toggle .v-icon.expanded {
  transform: rotate(180deg);
}

.artifact-content {
  border-top: 1px solid #ddd;
  background-color: #fff;
}

.artifact-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px;
  border: none;
  border-top: 1px solid #ddd;
  background: none;
  cursor: pointer;
  color: #666;
  transition: background-color 0.2s ease;
}

.artifact-footer:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.artifact-text {
  padding: 16px;
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
}

.artifact-markdown {
  padding: 16px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
}

.artifact-markdown h1, .artifact-markdown h2, .artifact-markdown h3 {
  margin-top: 0;
  margin-bottom: 12px;
  font-weight: 600;
}

.artifact-markdown h1 {
  font-size: 18px;
}

.artifact-markdown h2 {
  font-size: 16px;
}

.artifact-markdown h3 {
  font-size: 14px;
}

.artifact-markdown p {
  margin-bottom: 8px;
}

.artifact-markdown ul, .artifact-markdown ol {
  margin-bottom: 8px;
  padding-left: 20px;
}

.artifact-markdown li {
  margin-bottom: 4px;
}

.artifact-markdown strong {
  font-weight: 600;
}

.artifact-markdown em {
  font-style: italic;
}

.artifact-markdown code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.artifact-markdown pre {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.artifact-markdown blockquote {
  border-left: 4px solid #ddd;
  padding-left: 12px;
  margin-left: 0;
  color: #666;
}

.artifact-markdown table {
  border-collapse: collapse;
  width: 100%;
  margin: 12px 0;
  font-size: 13px;
}

.artifact-markdown th,
.artifact-markdown td {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.artifact-markdown th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.artifact-markdown tr:nth-child(even) {
  background-color: #f9f9f9;
}

.artifact-markdown tr:hover {
  background-color: #f0f0f0;
}

.artifact-markdown del {
  text-decoration: line-through;
  color: #999;
}

.artifact-markdown input[type="checkbox"] {
  margin-right: 6px;
}

.artifact-html {
  padding: 16px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  overflow-x: auto;
}

.artifact-html * {
  max-width: 100%;
}

.artifact-html table {
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 13px;
}

.artifact-html th,
.artifact-html td {
  border: 1px solid #ddd;
  padding: 8px 12px;
  text-align: left;
}

.artifact-html th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.artifact-html tr:nth-child(even) {
  background-color: #f9f9f9;
}

.artifact-html tr:hover {
  background-color: #f0f0f0;
}

.artifact-html pre {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.artifact-html code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

/* Dark theme styles */
.dark-theme .artifact-container {
  background-color: #2F3033;
  border-color: #555;
}

.dark-theme .artifact-content {
  background-color: #1a1a1a;
  border-top-color: #555;
}

.dark-theme .artifact-footer {
  border-top-color: #555;
  color: #aaa;
}

.dark-theme .artifact-footer:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.dark-theme .filename {
  color: #E0E0E0;
}

.dark-theme .filetype {
  color: #aaa;
}

.dark-theme .artifact-icon {
  color: #aaa;
}

.dark-theme .download-btn {
  color: #E0E0E0;
}

.dark-theme .download-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.dark-theme .artifact-text {
  color: #E0E0E0;
  background-color: #1a1a1a;
}

.dark-theme .artifact-markdown {
  color: #E0E0E0;
}

.dark-theme .artifact-markdown h1,
.dark-theme .artifact-markdown h2,
.dark-theme .artifact-markdown h3 {
  color: #E0E0E0;
}

.dark-theme .artifact-markdown code {
  background-color: #2a2a2a;
  color: #E0E0E0;
}

.dark-theme .artifact-markdown pre {
  background-color: #2a2a2a;
  color: #E0E0E0;
}

.dark-theme .artifact-markdown blockquote {
  border-left-color: #555;
  color: #ccc;
}

.dark-theme .artifact-markdown table {
  border-color: #555;
}

.dark-theme .artifact-markdown th,
.dark-theme .artifact-markdown td {
  border-color: #555;
}

.dark-theme .artifact-markdown th {
  background-color: #3a3a3a;
  color: #E0E0E0;
}

.dark-theme .artifact-markdown tr:nth-child(even) {
  background-color: #2a2a2a;
}

.dark-theme .artifact-markdown tr:hover {
  background-color: #404040;
}

.dark-theme .artifact-markdown del {
  color: #888;
}

.dark-theme .artifact-html {
  color: #E0E0E0;
}

.dark-theme .artifact-html table {
  border-color: #555;
}

.dark-theme .artifact-html th,
.dark-theme .artifact-html td {
  border-color: #555;
}

.dark-theme .artifact-html th {
  background-color: #3a3a3a;
  color: #E0E0E0;
}

.dark-theme .artifact-html tr:nth-child(even) {
  background-color: #2a2a2a;
}

.dark-theme .artifact-html tr:hover {
  background-color: #404040;
}

.dark-theme .artifact-html pre {
  background-color: #2a2a2a;
  color: #E0E0E0;
}

.dark-theme .artifact-html code {
  background-color: #2a2a2a;
  color: #E0E0E0;
}
</style>
